import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { CookieService } from 'ngx-cookie-service';
import { filter, map, Observable, tap, switchMap, ReplaySubject } from 'rxjs';
import { PersistState } from '@datorama/akita';

import { Models } from '../models/global.models';
import { SecurityProfileModel, SecurityProfileQuery } from '../loan-state/security-profile';
import { environment } from '../../environments/environment';
import { Router, UrlSerializer } from '@angular/router';
import { decodeJwt } from 'jose';
import { SessionStore } from '../core/state';

export interface SecurityProfileAuthToken {
  SecurityProfileId: string;
  OrganizationId: string;
  UserId: string;
  ByteLogin: string;
  UserOrgProfileId: string;
  RawUserOrgProfileId: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _persistStorage = inject<PersistState>('persistStorage' as any);
  private readonly _msalService = inject(MsalService);
  private readonly _http = inject(HttpClient);
  private readonly _securityProfileQuery = inject(SecurityProfileQuery);
  private readonly _cookieService = inject(CookieService);
  private readonly _router = inject(Router);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _urlSerializer = inject(UrlSerializer);

  private _securityProfileAuthToken = new ReplaySubject<string>(1);
  securityProfileAuthToken$ = this._securityProfileAuthToken.asObservable();

  private _baseUrl = `${environment.retail.baseUrl}/api`;

  resolveSecurityProfile(account: AccountInfo) {
    const token = localStorage.getItem(Models.LocalStorage.SecurityProfileAuthToken);

    if (!token) {
      this._router.navigateByUrl('/security-profiles');
      return;
    }

    const accountClaims = account.idTokenClaims as Models.Clear2IdTokenClaims;

    const claims = decodeJwt<SecurityProfileAuthToken>(token);

    if (!accountClaims.ByteLogin || !claims.ByteLogin) {
      localStorage.removeItem(Models.LocalStorage.SecurityProfileAuthToken);
      this._router.navigateByUrl('/security-profiles');
      return;
    }

    this._securityProfileAuthToken.next(token);
  }

  createSecurityProfileAuthToken(integratedMode?: boolean): Observable<Models.Session> {
    return this._securityProfileQuery.selectActive<SecurityProfileModel>().pipe(
      filter((securityProfile) => !!securityProfile),
      map((securityProfile: SecurityProfileModel) => ({
        securityProfileId: securityProfile.securityProfileId,
        organizationId: securityProfile.organizationId,
        userOrgProfileId: securityProfile.userOrgProfileId,
        businessChannel: securityProfile.businessChannel,
        integratedMode: integratedMode,
      })),
      switchMap((body) => this._http.post<Models.Session>(`${this._baseUrl}/session/securityprofileauthtoken`, body)),
      tap((token) => {
        this.saveSecurityProfileAuthToken(token.token);
      }),
    );
  }

  refreshSecurityProfileAuthToken(): Observable<Models.Session> {
    return this._http
      .post<Models.Session>(`${this._baseUrl}/session/securityprofileauthtoken/refresh`, {
        refreshToken: localStorage.getItem(Models.LocalStorage.SecurityProfileAuthToken),
      })
      .pipe(
        tap((token) => {
          this.saveSecurityProfileAuthToken(token.token);
        }),
      );
  }

  saveSecurityProfileAuthToken(token: string): void {
    localStorage.setItem(Models.LocalStorage.SecurityProfileAuthToken, token);
    this._securityProfileAuthToken.next(token);
  }

  logout() {
    const isIntegratedUrl = this.isIntegratedUrl();
    this._persistStorage.clearStore();
    this._cookieService.deleteAll();
    this._clearLocalStorage();
    if (isIntegratedUrl) {
      this._msalService.logoutRedirect({ postLogoutRedirectUri: null });
    } else {
      this._msalService.logoutRedirect();
    }
  }

  isIntegratedSession(): boolean {
    const integratedLoginContext = this._sessionStore.getValue()?.integratedLoginContext;
    const integratedUrl = new URLSearchParams(window.location.search)?.get('integrationContext');
    const integratedSessionActive = this._sessionStore.getValue()?.integratedSessionActive;

    return !!integratedLoginContext || !!integratedUrl || integratedSessionActive;
  }

  isIntegratedUrl(): boolean {
    const integratedUrl = new URLSearchParams(window.location.search)?.get('integrationContext');
    return !!integratedUrl;
  }

  getIntegratedTargetUrl(): string {
    const integratedUrl = this._urlSerializer.parse('/integrated-login');
    integratedUrl.queryParams.userOrgProfileId = this._sessionStore.getValue()?.integratedUserOrgProfileId;
    integratedUrl.queryParams.targetUrl = this._sessionStore.getValue()?.integratedTargetUrl;
    return integratedUrl.queryParams.targetUrl ? integratedUrl.toString() : null;
  }

  private _clearLocalStorage() {
    const keys = Object.values(Models.LocalStorage);
    for (const key of keys) {
      localStorage.removeItem(key);
    }
  }
}
